import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSocket } from '../../contexts/SocketContext';
import { ArrowLeft, Wallet, User, Users, TrendingUp } from 'lucide-react';
import api from '../../utils/api';
import { beijingToday } from '../../utils/datetime';
import AdminNotificationBell from '../../components/AdminNotificationBell';
import TabBar from '../../components/TabBar';

import { fmtMoney as fmt } from '../../utils/clipboard';

export default function AgentSettlementOverviewPage() {
  const navigate = useNavigate();
  const { subscribe } = useSocket();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState(beijingToday());

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/settlement/agent-settlement-overview', { params: { date } });
      setData(res.data);
    } catch (err) {
      console.error(err);
    } finally { setLoading(false); }
  }, [date]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (!subscribe) return;
    return subscribe((event) => {
      if (event === 'settlement_updated') fetchData();
    });
  }, [subscribe, fetchData]);

  return (
    <div className="page">
      <div className="header" style={{ display: 'flex', alignItems: 'center' }}>
        <button onClick={() => navigate(-1)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px 4px 0', display: 'flex', alignItems: 'center', color: 'var(--c-primary)' }}>
          <ArrowLeft size={18} />
        </button>
        <span style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Wallet size={16} /> 我的结算统计
        </span>
        <AdminNotificationBell />
      </div>

      <div style={{ padding: '12px 16px' }}>

        {/* 日期选择 */}
        <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <span style={{ fontSize: 13, color: 'var(--c-text-secondary)', flexShrink: 0 }}>查看日期</span>
          <input className="input" type="date" value={date} onChange={e => setDate(e.target.value)} style={{ flex: 1 }} />
        </div>

        {loading ? (
          <p style={{ textAlign: 'center', padding: 40, color: 'var(--c-text-secondary)' }}>加载中...</p>
        ) : !data ? null : (
          <>
            {/* 应收总额横幅 */}
            <div className="card gradient-blue" style={{ color: 'white', padding: 18 }}>
              <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                <TrendingUp size={13} /> 管理员应付我的总额
              </div>
              <div style={{ fontSize: 30, fontWeight: 800, letterSpacing: '-0.02em' }}>¥{fmt(data.totalReceivable)}</div>
              {data.personalBy && (data.personalBy.legacy.amount > 0 || data.personalBy.puzzle.amount > 0) && (
                <div style={{ display: 'flex', gap: 16, marginTop: 8, opacity: 0.95, fontSize: 13 }}>
                  <span>排五 ¥{fmt(data.personalBy.legacy.amount)}</span>
                  <span>证券 ¥{fmt(data.personalBy.puzzle.amount)}</span>
                  <span style={{ opacity: 0.7 }}>（个人报酬里）</span>
                </div>
              )}
              <div style={{ display: 'flex', gap: 20, marginTop: 10, opacity: 0.85, fontSize: 13 }}>
                <span>个人报酬 ¥{fmt(data.personal.amount)}</span>
                <span style={{ opacity: 0.8 }}>下级成本 ¥{fmt(data.sub.totalCost)}（已含在内，仅核对）</span>
              </div>
            </div>

            {/* 个人接量明细 */}
            <div className="card">
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
                <User size={15} color="var(--c-primary)" />
                <span style={{ fontSize: 15, fontWeight: 700 }}>我的接量报酬</span>
              </div>
              {data.personal.amount > 0 ? (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ fontSize: 13, color: 'var(--c-text-secondary)' }}>接量</span>
                    <span style={{ fontSize: 13 }}>
                      {data.personal.count} 条
                      = <strong style={{ color: 'var(--c-text)' }}>¥{fmt(data.personal.amount)}</strong>
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', background: 'rgba(52,199,89,0.08)', padding: '10px 12px', borderRadius: 8 }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--c-success)' }}>个人报酬合计</span>
                    <span style={{ fontSize: 16, fontWeight: 800, color: 'var(--c-success)' }}>¥{fmt(data.personal.amount)}</span>
                  </div>
                </>
              ) : (
                <p style={{ fontSize: 13, color: 'var(--c-text-secondary)', textAlign: 'center', padding: 12 }}>今日暂无个人结算</p>
              )}
            </div>

            {/* 下级用户明细 */}
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Users size={15} color="var(--c-primary)" />
                  <span style={{ fontSize: 15, fontWeight: 700 }}>下级用户明细</span>
                </div>
                {data.sub.count > 0 && (
                  <span style={{ fontSize: 12, color: 'var(--c-text-secondary)' }}>{data.sub.count} 人</span>
                )}
              </div>

              {data.sub.count === 0 ? (
                <p style={{ fontSize: 13, color: 'var(--c-text-secondary)', textAlign: 'center', padding: '0 16px 16px' }}>今日无下级结算记录</p>
              ) : (
                <>
                  {data.sub.records.map((r, i) => (
                    <div key={r.id} style={{
                      padding: '12px 16px',
                      borderTop: '0.5px solid var(--c-border)',
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                        <span style={{ fontSize: 14, fontWeight: 600, display: 'inline-flex', alignItems: 'center' }}>
                          <span style={{ fontSize: 10, fontWeight: 700, borderRadius: 5, padding: '1px 6px', marginRight: 6, background: r.business === 'puzzle' ? 'rgba(175,82,222,0.14)' : 'rgba(0,122,255,0.12)', color: r.business === 'puzzle' ? '#8944ab' : 'var(--c-primary)' }}>{r.business === 'puzzle' ? '证券' : '排五'}</span>
                          {r.user_name || r.user_phone}
                        </span>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: 13, color: 'var(--c-text-secondary)' }}>管理员付我</div>
                          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--c-primary)' }}>¥{fmt(r.base_cost)}</div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 12, fontSize: 12, color: 'var(--c-text-secondary)' }}>
                        <span>{r.count || 0} 条{r.price != null && r.price > 0 ? ` × ¥${r.price}` : ''} = ¥{fmt(r.amount ?? r.total_amount)}</span>
                      </div>
                      <div style={{ display: 'flex', gap: 16, marginTop: 6, fontSize: 12 }}>
                        <span style={{ color: 'var(--c-text-secondary)' }}>付给用户 <span style={{ color: 'var(--c-text)' }}>¥{fmt(r.total_amount)}</span></span>
                        <span style={{ color: 'var(--c-accent)' }}>我的佣金 ¥{fmt(r.profit)}</span>
                      </div>
                    </div>
                  ))}

                  {/* 下级汇总 */}
                  <div style={{ padding: '12px 16px', borderTop: '0.5px solid var(--c-border)', background: 'var(--c-primary-light)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                      <span style={{ fontSize: 13, color: 'var(--c-text-secondary)' }}>付给下级用户合计</span>
                      <span style={{ fontSize: 13, fontWeight: 600 }}>¥{fmt(data.sub.totalUserAmount)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                      <span style={{ fontSize: 13, color: 'var(--c-text-secondary)' }}>管理员付我（基准价）</span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--c-primary)' }}>¥{fmt(data.sub.totalCost)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: 13, color: 'var(--c-accent)', fontWeight: 600 }}>我的差价佣金</span>
                      <span style={{ fontSize: 15, fontWeight: 800, color: 'var(--c-accent)' }}>¥{fmt(data.sub.commission)}</span>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* 总结 */}
            {data.totalReceivable > 0 && (
              <div className="card" style={{ background: 'var(--c-primary-light)', border: '1px solid rgba(0,122,255,0.2)' }}>
                <div style={{ fontSize: 13, color: 'var(--c-text-secondary)', marginBottom: 8 }}>本日结算总结</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ fontSize: 13 }}>管理员实际付给我（个人报酬）</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--c-success)' }}>¥{fmt(data.personal.amount)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                  <span style={{ fontSize: 12, color: 'var(--c-text-secondary)' }}>其中下级成本（已包含在上面，仅核对）</span>
                  <span style={{ fontSize: 12, color: 'var(--c-text-secondary)' }}>¥{fmt(data.sub.totalCost)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 10, borderTop: '0.5px solid rgba(0,122,255,0.2)' }}>
                  <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--c-primary)' }}>管理员应付我总额</span>
                  <span style={{ fontSize: 18, fontWeight: 800, color: 'var(--c-primary)' }}>¥{fmt(data.totalReceivable)}</span>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      <TabBar mode="admin" />
    </div>
  );
}
